import { html } from 'onefold';
import { CodeBlock } from '../../components/CodeBlock';
import { Playground } from '../../components/Playground';

export function UtilitiesPage(): Node {
  return html`
    <div>
      <h1>Utilities</h1>
      <p>onefold ships common utility functions to reduce external dependencies. All are tree-shakeable — only imported functions are bundled.</p>

      <h2>Date & Time</h2>
      ${CodeBlock(`import { formatDate, timeAgo } from 'onefold/utils';

formatDate(new Date(), 'short')    // 'Jul 15, 2026'
formatDate(new Date(), 'iso')      // '2026-07-15'

timeAgo(new Date(Date.now() - 60000))   // '1 minute ago'
timeAgo(new Date(Date.now() - 3600000)) // '1 hour ago'`)}

      <h2>Formatting</h2>
      ${CodeBlock(`import { formatCurrency, formatNumber, truncate, slugify, pluralize, capitalize } from 'onefold/utils';

formatCurrency(1234.5, 'USD')  // '$1,234.50'
formatCurrency(999, 'EUR')     // '€999.00'
formatNumber(123456)           // '123,456'

truncate('Hello World this is long', 20)  // 'Hello World this ...'
slugify('Hello World!')      // 'hello-world'
capitalize('hello')          // 'Hello'
pluralize('item', 1)         // 'item'
pluralize('item', 5)         // 'items'`)}

      <h2>Function Utilities</h2>
      ${CodeBlock(`import { debounce, throttle, pipe } from 'onefold/utils';

const search = debounce((query) => fetchResults(query), 300);
const handleScroll = throttle(() => updatePosition(), 100);

// Pipe — passes value through functions left to right
const result = pipe('  Hello World  ',
  (s: string) => s.trim(),
  (s: string) => s.toLowerCase(),
  (s: string) => s.replace(/\\s+/g, '-'),
);
// result === 'hello-world'`)}

      <h2>API Reference</h2>
      <table>
        <tr><th>Function</th><th>Signature</th><th>Description</th></tr>
        <tr><td><code>formatDate</code></td><td>(date, format) => string</td><td>Format a Date ('short', 'long', 'iso', 'datetime').</td></tr>
        <tr><td><code>timeAgo</code></td><td>(date) => string</td><td>Human-readable relative time.</td></tr>
        <tr><td><code>formatCurrency</code></td><td>(amount, currency) => string</td><td>Format number as currency.</td></tr>
        <tr><td><code>formatNumber</code></td><td>(num) => string</td><td>Format with locale separators.</td></tr>
        <tr><td><code>truncate</code></td><td>(str, maxLen) => string</td><td>Truncate with ellipsis.</td></tr>
        <tr><td><code>slugify</code></td><td>(str) => string</td><td>URL-safe slug from string.</td></tr>
        <tr><td><code>capitalize</code></td><td>(str) => string</td><td>Uppercase first letter.</td></tr>
        <tr><td><code>pluralize</code></td><td>(word, count, plural?) => string</td><td>Pluralize based on count.</td></tr>
        <tr><td><code>debounce</code></td><td>(fn, ms) => fn</td><td>Delay execution until idle.</td></tr>
        <tr><td><code>throttle</code></td><td>(fn, ms) => fn</td><td>Limit execution frequency.</td></tr>
        <tr><td><code>pipe</code></td><td>(...fns) => fn</td><td>Left-to-right function composition.</td></tr>
      </table>

      <h2>Try It</h2>

      ${Playground(`import { createSignal, html, mount } from 'onefold';
import { formatDate, timeAgo, formatCurrency, formatNumber, truncate, slugify, pluralize, capitalize, debounce, throttle, pipe } from 'onefold/utils';

function App() {
  const input = createSignal('Hello World! This is OneFold.');
  const amount = createSignal(1234.56);
  const count = createSignal(3);
  const throttleCount = createSignal(0);

  const debouncedLog = debounce((q) => console.log('Debounced:', q), 500);
  const throttledIncrement = throttle(() => throttleCount.set(n => n + 1), 300);

  const transform = (s) => pipe(s,
    (s) => s.trim(),
    (s) => s.toLowerCase(),
    (s) => s.replace(/[^a-z0-9]+/g, '-'),
  );

  const now = new Date();
  const fiveMinAgo = new Date(Date.now() - 5 * 60 * 1000);
  const twoHoursAgo = new Date(Date.now() - 2 * 60 * 60 * 1000);

  return html\`
    <div>
      <h3>Utilities — Live API</h3>

      <div style="display:flex;gap:12px;margin-bottom:12px">
        <div style="flex:1">
          <label style="font-size:12px;color:#666">Text input</label>
          <input oninput=\${(e) => { input.set(e.target.value); debouncedLog(e.target.value); }} style="width:100%" placeholder="Type here..." />
        </div>
        <div>
          <label style="font-size:12px;color:#666">Amount</label>
          <input type="number" oninput=\${(e) => amount.set(Number(e.target.value))} style="width:90px" placeholder="1234" />
        </div>
        <div>
          <label style="font-size:12px;color:#666">Count</label>
          <input type="number" oninput=\${(e) => count.set(Number(e.target.value))} style="width:70px" placeholder="3" />
        </div>
      </div>

      <table style="width:100%;font-size:13px;border-collapse:collapse">
        <tr style="border-bottom:1px solid #e5e7eb"><td style="padding:6px"><strong>slugify</strong></td><td style="padding:6px;font-family:monospace">\${() => slugify(input())}</td></tr>
        <tr style="border-bottom:1px solid #e5e7eb"><td style="padding:6px"><strong>truncate(20)</strong></td><td style="padding:6px;font-family:monospace">\${() => truncate(input(), 20)}</td></tr>
        <tr style="border-bottom:1px solid #e5e7eb"><td style="padding:6px"><strong>capitalize</strong></td><td style="padding:6px;font-family:monospace">\${() => capitalize(input())}</td></tr>
        <tr style="border-bottom:1px solid #e5e7eb"><td style="padding:6px"><strong>pipe (transform)</strong></td><td style="padding:6px;font-family:monospace">\${() => transform(input())}</td></tr>
        <tr style="border-bottom:1px solid #e5e7eb"><td style="padding:6px"><strong>pluralize('item')</strong></td><td style="padding:6px;font-family:monospace">\${() => count() + ' ' + pluralize('item', count())}</td></tr>
        <tr style="border-bottom:1px solid #e5e7eb"><td style="padding:6px"><strong>formatCurrency(USD)</strong></td><td style="padding:6px;font-family:monospace">\${() => formatCurrency(amount(), 'USD')}</td></tr>
        <tr style="border-bottom:1px solid #e5e7eb"><td style="padding:6px"><strong>formatNumber</strong></td><td style="padding:6px;font-family:monospace">\${() => formatNumber(amount())}</td></tr>
        <tr style="border-bottom:1px solid #e5e7eb"><td style="padding:6px"><strong>formatDate(now)</strong></td><td style="padding:6px;font-family:monospace">\${formatDate(now, 'short')}</td></tr>
        <tr style="border-bottom:1px solid #e5e7eb"><td style="padding:6px"><strong>timeAgo(5 min)</strong></td><td style="padding:6px;font-family:monospace">\${timeAgo(fiveMinAgo)}</td></tr>
        <tr><td style="padding:6px"><strong>timeAgo(2 hours)</strong></td><td style="padding:6px;font-family:monospace">\${timeAgo(twoHoursAgo)}</td></tr>
        <tr style="border-bottom:1px solid #e5e7eb"><td style="padding:6px"><strong>throttle (click fast!)</strong></td><td style="padding:6px;font-family:monospace"><button onclick=\${throttledIncrement} style="font-size:11px;padding:2px 6px">Click rapidly</button> counted: \${() => throttleCount()}</td></tr>
      </table>

      <p style="font-size:11px;color:#94a3b8;margin-top:12px">debounce: type in input, check Console tab. throttle: click rapidly — increments at most once per 300ms. pipe: combines trim + lowercase + hyphenate.</p>
    </div>
  \`;
}

mount(App(), document.getElementById('app'));`, 'onefold/utils — All Utilities Live')}

      <h2>Next Steps</h2>
      <ul>
        <li><a href="/core/templates">Templates</a> — the html tagged template literal</li>
        <li><a href="/core/signals">Signals</a> — reactive primitives powering the UI</li>
      </ul>
    </div>
  `;
}
